import React, { useState, useEffect } from "react";
import "./limits.scss";
import {
  Dropdown,
  Button,
  TextInput,
  Slider,
  IconButton,
  Toggle,
  NumberInput,
} from "@carbon/react";
import { TrashCan, Checkmark, Close } from "@carbon/icons-react";
import { useSetAreaConfigItem } from "../../../hooks/useSetAreaConfigItem";
import { useDeleteAreaConfigItem } from "../../../hooks/useDeleteAreaConfigItem";
import { useTranslation } from "react-i18next";

export default function LimitItem({
  config,
  currentArea,
  areasData,
  setAreasData,
  currentAreaLimits,
  currentAreaGameTypeLimits,
  changingLimits,
  setChangingLimits,
  changeAll,
  setChangeAll,
  onValidationChange,
  isModEnabled,
  currentLimit,
  setCurrentLimit,
}) {
  const { t } = useTranslation();

  const [toBeDeleted, setToBeDeleted] = useState(false);
  const [limitConfig, setLimitConfig] = useState({
    gameType: config.gameType,
    limit: config.bbCents,
    targetMode: config.targetMode,
    targetValue: config.targetValue,
    teamplay: config.teamplay,
    flow: config.flow,
    fprofilePower: config.fprofilePower,
    id: config.id,
  });

  const [validationGameType, setValidationGameType] = useState({
    invalid: false,
    invalidText: "",
  });
  const [validationLimit, setValidationLimit] = useState({
    invalid: false,
    invalidText: "",
  });
  const [validationTargetValue, setValidationTargetValue] = useState({
    invalid: false,
    invalidText: "",
  });
  const [validationFprofileValue, setValidationFprofileValue] = useState({
    invalid: false,
    invalidText: "",
  });
  const isCurrentLimit = currentLimit?.id === config.id;

  const mutationSet = useSetAreaConfigItem();
  const mutationDelete = useDeleteAreaConfigItem();

  const updateNewLimit = (key, value) => {
    setLimitConfig((prev) => ({ ...prev, [key]: value }));
    if (!config.hasOwnProperty("newLimit")) {
      setChangingLimits((prevState) => {
        if (!prevState[currentArea].includes(limitConfig.id)) {
          return {
            ...prevState,
            [currentArea]: [...prevState[currentArea], limitConfig.id],
          };
        }
        return prevState;
      });
    }
  };

  useEffect(() => {
    if (limitConfig.gameType === "") {
      setValidationGameType({
        invalid: true,
        invalidText: "eco.limitItem.validation.noType",
      });
      onValidationChange(limitConfig.id, true);
    } else {
      setValidationGameType({ invalid: false, invalidText: "" });
      onValidationChange(limitConfig.id, false);
    }
  }, [limitConfig.gameType, limitConfig.id, setValidationGameType]);

  useEffect(() => {
    const isDuplicate = currentAreaLimits.some(
      (obj) =>
        obj.id !== limitConfig.id &&
        obj.bbCents === +limitConfig.limit &&
        obj.gameType === limitConfig.gameType,
    );

    if (limitConfig.limit === "") {
      setValidationLimit({
        invalid: true,
        invalidText: "eco.limitItem.validation.noLimit",
      });
      onValidationChange(limitConfig.id, true);
    } else if (isDuplicate) {
      setValidationLimit({
        invalid: true,
        invalidText: "eco.limitItem.validation.existLimit",
      });
      onValidationChange(limitConfig.id, true);
    } else {
      setValidationLimit({ invalid: false, invalidText: "" });
      onValidationChange(limitConfig.id, false);
    }
  }, [
    limitConfig.limit,
    limitConfig.gameType,
    setValidationLimit,
    currentAreaLimits,
  ]);

  useEffect(() => {
    if (limitConfig.targetValue === "" || limitConfig.targetValue === "-") {
      setValidationTargetValue({
        invalid: true,
        invalidText: "eco.limitItem.validation.noWinrate",
      });
      onValidationChange(limitConfig.id, true);
    } else if (
      (limitConfig.targetMode === "WINRATE" &&
        (limitConfig.targetValue < -10 || limitConfig.targetValue > 100)) ||
      (limitConfig.targetMode === "LOSSRATE" &&
        (limitConfig.targetValue < -100 || limitConfig.targetValue > 5))
    ) {
      setValidationTargetValue({
        invalid: true,
        invalidText: "eco.limitItem.validation.invalidWinrate",
      });
      onValidationChange(limitConfig.id, true);
    } else if (
      limitConfig.fprofilePower < 0 ||
      limitConfig.fprofilePower > 1 ||
      !(
        Math.abs(
          limitConfig.fprofilePower * 100 -
            Math.round(limitConfig.fprofilePower * 100),
        ) < 1e-9
      )
    ) {
      setValidationFprofileValue({
        invalid: true,
        invalidText: "eco.limitItem.validation.invalidFProfilePower",
      });
      onValidationChange(limitConfig.id, true);
    } else {
      setValidationTargetValue({ invalid: false, invalidText: "" });
      setValidationFprofileValue({ invalid: false, invalidText: "" });
      onValidationChange(limitConfig.id, false);
    }
  }, [
    limitConfig.targetValue,
    setValidationTargetValue,
    limitConfig.fprofilePower,
    setValidationFprofileValue,
  ]);

  const handlePreventInvalidKeys = (evt) => {
    if (["e", "E", "-", "+"].includes(evt.key)) {
      evt.preventDefault();
    }
  };

  const getTranslatedText = (item) => {
    if (!item) return "";

    switch (item) {
      case "MANUAL":
        return t("eco.limitItem.mode.manual");
      case "WINRATE":
        return t("eco.limitItem.mode.winrate");
      case "LOSSRATE":
        return t("eco.limitItem.mode.loserate");
      default:
        break;
    }
  };

  const setValueByMode = (mode) => {
    let newValue;

    if (config.targetMode === mode) {
      newValue = config.targetValue;
    } else {
      switch (mode) {
        case "MANUAL":
          newValue = 0;
          break;
        case "WINRATE":
          newValue = 0;
          break;
        case "LOSSRATE":
          newValue = 0;
          break;
        default:
          break;
      }
    }
    updateNewLimit("targetValue", newValue);
  };

  const handleDelete = () => {
    const updatedConfigs = areasData[currentArea].configs.filter(
      (obj) => obj.id !== limitConfig.id,
    );
    setAreasData((prevState) => ({
      ...prevState,
      [currentArea]: { ...prevState[currentArea], configs: updatedConfigs },
    }));

    mutationDelete.mutate({
      areaId: currentArea,
      gameType: limitConfig.gameType,
      bbCents: limitConfig.limit,
    });
  };

  const handleCreateLimit = () => {
    if (config.hasOwnProperty("newLimit")) {
      const updatedConfigs = areasData[currentArea].configs.map((obj) =>
        obj.id === limitConfig.id
          ? {
              gameType: limitConfig.gameType,
              bbCents: limitConfig.limit,
              targetMode: limitConfig.targetMode,
              targetValue: limitConfig.targetValue,
              teamplay: limitConfig.teamplay,
              flow: limitConfig.flow,
              fprofilePower: limitConfig.fprofilePower,
              id: limitConfig.id,
            }
          : obj,
      );
      setAreasData((prevState) => ({
        ...prevState,
        [currentArea]: { ...prevState[currentArea], configs: updatedConfigs },
      }));
      mutationSet.mutate({
        areaId: currentArea,
        gameType: limitConfig.gameType,
        bbCents: limitConfig.limit,
        targetMode: limitConfig.targetMode,
        targetValue: limitConfig.targetValue,
        teamplay: limitConfig.teamplay,
        flow: limitConfig.flow,
        fprofilePower: limitConfig.fprofilePower,
      });
    }
  };

  const handleCancelCreationLimit = () => {
    const updatedConfigs = areasData[currentArea].configs.filter(
      (obj) => obj.id !== limitConfig.id,
    );
    setAreasData((prevState) => ({
      ...prevState,
      [currentArea]: { ...prevState[currentArea], configs: updatedConfigs },
    }));
  };

  const handleEditLimit = () => {
    if (changingLimits[currentArea].some((item) => item === limitConfig.id)) {
      const updatedConfigs = areasData[currentArea].configs.map((obj) =>
        obj.id === limitConfig.id
          ? {
              gameType: limitConfig.gameType,
              bbCents: limitConfig.limit,
              targetMode: limitConfig.targetMode,
              targetValue: limitConfig.targetValue,
              teamplay: limitConfig.teamplay,
              flow: limitConfig.flow,
              fprofilePower: limitConfig.fprofilePower,
              id: limitConfig.id,
            }
          : obj,
      );
      setAreasData((prevState) => ({
        ...prevState,
        [currentArea]: { ...prevState[currentArea], configs: updatedConfigs },
      }));
      setChangingLimits((prevState) => {
        const { [currentArea]: keyArray, ...restState } = prevState;

        return {
          ...restState,
          [currentArea]: keyArray.filter((item) => item !== limitConfig.id),
        };
      });
      if (config.bbCents !== limitConfig.limit) {
        mutationDelete.mutate({
          areaId: currentArea,
          gameType: config.gameType,
          bbCents: config.bbCents,
        });
        mutationSet.mutate({
          areaId: currentArea,
          gameType: limitConfig.gameType,
          bbCents: limitConfig.limit,
          targetMode: limitConfig.targetMode,
          targetValue: limitConfig.targetValue,
          teamplay: limitConfig.teamplay,
          flow: limitConfig.flow,
          fprofilePower: limitConfig.fprofilePower,
        });
      } else {
        mutationSet.mutate({
          areaId: currentArea,
          gameType: limitConfig.gameType,
          bbCents: limitConfig.limit,
          targetMode: limitConfig.targetMode,
          targetValue: limitConfig.targetValue,
          teamplay: limitConfig.teamplay,
          flow: limitConfig.flow,
          fprofilePower: limitConfig.fprofilePower,
        });
      }
    }
  };

  const handleCancelEditLimit = () => {
    setChangingLimits((prevState) => {
      const { [currentArea]: keyArray, ...restState } = prevState;

      return {
        ...restState,
        [currentArea]: keyArray.filter((item) => item !== limitConfig.id),
      };
    });
    setLimitConfig({
      gameType: config.gameType,
      limit: config.bbCents,
      targetMode: config.targetMode,
      targetValue: config.targetValue,
      teamplay: config.teamplay,
      flow: config.flow,
      fprofilePower: config.fprofilePower,
      id: config.id,
    });
  };

  useEffect(() => {
    if (changeAll === "confirm") {
      handleEditLimit();
      setChangeAll("");
    } else if (changeAll === "cancel") {
      handleCancelEditLimit();
      setChangeAll("");
    } else return;
  }, [changeAll]);

  return (
    <div
      className={`limits_limit ${
        config.hasOwnProperty("newLimit")
          ? "new-limit"
          : changingLimits[currentArea].some((item) => item === limitConfig.id)
            ? "edited-limit"
            : ""
      } ${isCurrentLimit ? "current-limit" : ""}`}
      onClick={() => setCurrentLimit(config)}
    >
      <div className="limits_limit_select">
        <div className="limits_limit_select_text">
          <Dropdown
            id={`dropdown-gameType-${currentArea}-${config.id}`}
            titleText=""
            size="sm"
            label={t("eco.limitItem.selection.type")}
            items={Object.keys(currentAreaGameTypeLimits) || []}
            selectedItem={limitConfig.gameType}
            onChange={({ selectedItem }) => {
              updateNewLimit("gameType", selectedItem);
            }}
            translateWithId={(messageId) => {
              if (messageId === "close.menu")
                return t("main.toolbar.tooltips.closeMenu");
              if (messageId === "open.menu")
                return t("main.toolbar.tooltips.openMenu");
            }}
            invalid={validationGameType.invalid}
            invalidText={
              validationGameType.invalidText
                ? t(validationGameType.invalidText)
                : ""
            }
          />
          <Dropdown
            id={`dropdown-limit-${currentArea}-${config.id}`}
            titleText=""
            size="sm"
            label={t("eco.limitItem.selection.limit")}
            items={currentAreaGameTypeLimits[limitConfig.gameType] || []}
            selectedItem={limitConfig.limit}
            onChange={({ selectedItem }) => {
              updateNewLimit("limit", selectedItem);
            }}
            translateWithId={(messageId) => {
              if (messageId === "close.menu")
                return t("main.toolbar.tooltips.closeMenu");
              if (messageId === "open.menu")
                return t("main.toolbar.tooltips.openMenu");
            }}
            invalid={validationLimit.invalid}
            invalidText={
              validationLimit.invalidText ? t(validationLimit.invalidText) : ""
            }
          />
        </div>
        <div className="limits_limit_select_buttons">
          {!config.hasOwnProperty("newLimit") &&
          !changingLimits[currentArea].some(
            (item) => item === limitConfig.id,
          ) ? (
            !toBeDeleted ? (
              <IconButton
                kind="ghost"
                label={t("eco.limitItem.buttons.delete")}
                size="sm"
                autoAlign
                onClick={() => setToBeDeleted(true)}
              >
                <TrashCan
                  className="limits_limit_select_buttons_delete"
                  size={20}
                />
              </IconButton>
            ) : (
              <div className="limits_limit_select_buttons_confirm_delete">
                <IconButton
                  kind="ghost"
                  label={t("eco.limitItem.buttons.confirm")}
                  size="sm"
                  autoAlign
                  onClick={handleDelete}
                >
                  <Checkmark size={20} />
                </IconButton>
                <IconButton
                  kind="ghost"
                  label={t("eco.limitItem.buttons.cancel")}
                  size="sm"
                  autoAlign
                  onClick={() => setToBeDeleted(false)}
                >
                  <Close size={20} />
                </IconButton>
              </div>
            )
          ) : null}
        </div>
      </div>
      <div>
        {isModEnabled && (
          <Dropdown
            id={`dropdown-mode-${currentArea}-${config.id}`}
            titleText=""
            size="sm"
            label={t("eco.limitItem.selection.mode")}
            items={["MANUAL", "WINRATE", "LOSSRATE"]}
            itemToString={getTranslatedText}
            selectedItem={limitConfig.targetMode}
            onChange={({ selectedItem }) => {
              updateNewLimit("targetMode", selectedItem);
              setValueByMode(selectedItem);
            }}
            translateWithId={(messageId) => {
              if (messageId === "close.menu")
                return t("main.toolbar.tooltips.closeMenu");
              if (messageId === "open.menu")
                return t("main.toolbar.tooltips.openMenu");
            }}
          />
        )}
      </div>
      <div>
        {limitConfig.targetMode === "MANUAL" && (
          <Slider
            id={`slider-${currentArea}-${config.id}`}
            labelText=""
            value={8 - limitConfig.targetValue * 10}
            min={0}
            max={8}
            step={1}
            noValidate
            hideTextInput={true}
            onRelease={(e) => updateNewLimit("targetValue", (8 - e.value) / 10)}
            formatLabel={(val) => {
              if (val < 1) {
                return `${t("eco.limitItem.power.weak")}`;
              } else if (val > 7) {
                return `${t("eco.limitItem.power.strong")}`;
              }
              return val;
            }}
          />
        )}
        {limitConfig.targetMode === "WINRATE" && (
          <div
            className={`limits_limit_botStrength ${
              limitConfig.targetValue > -1 ? "" : "invalidNumber"
            }`}
          >
            <div className="limits_limit_botStrength_text">Target Bb/100</div>
            <TextInput
              id={`text-input-${currentArea}-${config.id}-1`}
              size="sm"
              labelText=""
              type="text"
              value={limitConfig.targetValue}
              onChange={(e) => {
                const value = e.target.value;
                if (/^-?(0|[1-9]\d*)?$/.test(value)) {
                  updateNewLimit(
                    "targetValue",
                    value === "-" || value === "" ? value : Number(value),
                  );
                }
              }}
              className={`${
                +limitConfig.targetValue > -1 ? "" : "invalidNumber"
              }`}
              invalid={validationTargetValue.invalid}
              invalidText={
                validationTargetValue.invalidText
                  ? t(validationTargetValue.invalidText)
                  : ""
              }
            />
          </div>
        )}
        {limitConfig.targetMode === "LOSSRATE" && (
          <div
            className={`limits_limit_botStrength ${
              limitConfig.targetValue < 1 ? "" : "invalidNumber"
            }`}
          >
            <div className="limits_limit_botStrength_text">Target Bb/100</div>
            <TextInput
              id={`text-input-${currentArea}-${config.id}-2`}
              size="sm"
              labelText=""
              type="text"
              value={limitConfig.targetValue}
              onChange={(e) => {
                const value = e.target.value;
                if (/^-?(0|[1-9]\d*)?$/.test(value)) {
                  updateNewLimit(
                    "targetValue",
                    value === "-" || value === "" ? value : Number(value),
                  );
                }
              }}
              className={`${
                +limitConfig.targetValue < 1 ? "" : "invalidNumber"
              }`}
              invalid={validationTargetValue.invalid}
              invalidText={
                validationTargetValue.invalidText
                  ? t(validationTargetValue.invalidText)
                  : ""
              }
            />
          </div>
        )}
      </div>

      <div className="limits_limit_f-profile">
        {limitConfig.fprofilePower !== null && (
          <NumberInput
            id={`number-input-${currentArea}-${config.id}`}
            label="F-profile"
            max={1}
            min={0}
            allowEmpty
            value={limitConfig.fprofilePower}
            onChange={(e) => {
              const value = e.target.value;
              const num = Number(value);

              if (!isNaN(num)) {
                updateNewLimit("fprofilePower", num);
              }
            }}
            onKeyDown={handlePreventInvalidKeys}
            size="sm"
            step={0.01}
            hideSteppers
            invalid={validationFprofileValue.invalid}
            invalidText={
              validationFprofileValue.invalidText
                ? t(validationFprofileValue.invalidText)
                : ""
            }
          />
        )}
      </div>

      <div className="limits_limit_toggle">
        {limitConfig.teamplay !== null && (
          <Toggle
            labelText={t("eco.limitItem.toggle.teamplay")}
            hideLabel
            toggled={limitConfig.teamplay}
            id={`toggle-teamplay-${currentArea}-${config.id}`}
            onToggle={() => updateNewLimit("teamplay", !limitConfig.teamplay)}
          />
        )}
        {limitConfig.flow !== null && (
          <Toggle
            labelText={t("eco.limitItem.toggle.giveInToFish")}
            hideLabel
            toggled={limitConfig.flow}
            id={`toggle-flow-${currentArea}-${config.id}`}
            onToggle={() => updateNewLimit("flow", !limitConfig.flow)}
          />
        )}
      </div>
      {config.hasOwnProperty("newLimit") && (
        <div className="limits_limit_edit">
          {" "}
          <Button
            size="sm"
            kind="primary"
            onClick={handleCreateLimit}
            disabled={
              !limitConfig.limit ||
              validationLimit.invalid ||
              validationTargetValue.invalid
            }
          >
            {t("eco.limitItem.buttons.create")}
          </Button>
          <Button kind="ghost" size="sm" onClick={handleCancelCreationLimit}>
            {t("eco.limitItem.buttons.cancel")}
          </Button>
        </div>
      )}
      {changingLimits[currentArea].some((item) => item === limitConfig.id) &&
        !config.hasOwnProperty("newLimit") && (
          <div className="limits_limit_edit">
            {" "}
            <Button
              size="sm"
              kind="primary"
              onClick={handleEditLimit}
              disabled={
                !limitConfig.limit ||
                validationLimit.invalid ||
                validationTargetValue.invalid ||
                validationFprofileValue.invalid
              }
            >
              {t("eco.limitItem.buttons.confirm")}
            </Button>
            <Button kind="ghost" size="sm" onClick={handleCancelEditLimit}>
              {t("eco.limitItem.buttons.cancel")}
            </Button>
          </div>
        )}
    </div>
  );
}
